/**
 * Service registry — the four disciplines and the child pages under each.
 *
 * Services are disciplines ("how the work is done here"); growth systems are
 * the packages that bundle them. A service's depth lives at
 * /services/<service>/<topic>, one static folder per LIVE topic, the same way
 * /tools/<slug> works: this file owns identity, intent, metadata and the link
 * graph, and each page owns its body copy. Pages rendered purely from registry
 * data would read as one template with the nouns swapped — the thin-content
 * signal the site is trying to escape.
 *
 * A PLANNED topic is registered so its intent is claimed and guarded before the
 * page exists. It has no folder, no nav entry and no sitemap row.
 *
 * `targetQuery` is the one query a page owns. `notFor` names queries the page
 * must NOT chase and the page that owns them instead. `npm run verify:registry`
 * fails when two pages anywhere on the site claim the same `targetQuery`.
 * See docs/page-intent-map.md.
 *
 * Pure data plus pure helpers: loaded by scripts/verify-registry.ts under tsx.
 */
import type { Metadata } from 'next'
import type { GrowthSystemId } from './growth-systems'
import { pageMetadata } from './metadata'

export type ServiceSlug = 'content-strategy' | 'seo' | 'paid-media' | 'measurement'

export type Service = {
  slug: ServiceSlug
  name: string
  /** For labels like "All SEO services", where the full name reads badly. */
  shortName: string
  summary: string
  targetQuery: string
}

/** Hub order. */
export const SERVICES: readonly Service[] = [
  {
    slug: 'content-strategy',
    shortName: 'Content Strategy',
    name: 'Content Strategy',
    summary:
      'The planning, execution, and measurement of content marketing to achieve specific business goals.',
    targetQuery: 'content strategy services',
  },
  {
    slug: 'seo',
    shortName: 'SEO',
    name: 'Search Engine Optimization',
    summary:
      'Continuously optimizing webpages to improve visibility, page experience, and rankings in search engines.',
    targetQuery: 'seo services',
  },
  {
    slug: 'paid-media',
    shortName: 'Paid Media',
    name: 'Paid Media',
    summary:
      'Strategic, objective-based advertising aimed at targeted audiences on selected platforms.',
    targetQuery: 'paid media agency',
  },
  {
    slug: 'measurement',
    shortName: 'Measurement',
    name: 'Measurement',
    summary:
      'Conversion tracking, attribution and reporting that tie content, search and paid media to revenue.',
    targetQuery: 'marketing analytics services',
  },
]

export const SERVICES_HUB = '/services'

export function servicePath(slug: ServiceSlug): string {
  return `${SERVICES_HUB}/${slug}`
}

export function getService(slug: ServiceSlug): Service {
  const service = SERVICES.find((s) => s.slug === slug)
  if (!service) throw new Error(`Unknown service: ${slug}`)
  return service
}

/** The other services, for a service page's "Related services" section. */
export function siblingServices(slug: ServiceSlug): Service[] {
  return SERVICES.filter((s) => s.slug !== slug)
}

/** "Must not target X — `ownedBy` owns it." */
export type IntentGuard = { query: string; ownedBy: string }

type TopicBase = {
  service: ServiceSlug
  slug: string
  /** Short label for nav, cards and breadcrumbs. */
  name: string
  targetQuery: string
  notFor: IntentGuard[]
}

export type PlannedTopic = TopicBase & {
  status: 'planned'
  /** Why it is not live yet — usually "no proof on the site". */
  note?: string
}

export type LiveTopic = TopicBase & {
  status: 'live'
  /** One sentence for cards, nav and parent-page links. */
  summary: string
  /** Page's own title; the root template appends " | Refinity.io". */
  metaTitle: string
  /** Meta description and Service JSON-LD description. */
  description: string
  h1: string
  secondaryQueries?: string[]
  /** Growth systems this discipline feeds. Links UP. */
  growthSystems: GrowthSystemId[]
  /** Stack tools the page shows (slugs in lib/stack.ts). */
  stack?: string[]
  /** Sanity learnPost slugs this page links DOWN to. */
  learnSlugs?: string[]
  /** ISO date of the last material copy change; drives sitemap lastmod. */
  reviewed: string
}

export type ServiceTopic = PlannedTopic | LiveTopic

export const SERVICE_TOPICS: readonly ServiceTopic[] = [
  // --- SEO ---
  {
    service: 'seo',
    slug: 'on-page-seo',
    name: 'On-Page SEO',
    targetQuery: 'on-page seo services',
    notFor: [
      { query: 'seo services', ownedBy: '/services/seo' },
      { query: 'on-page seo checker', ownedBy: '/tools/on-page-seo-checker' },
      {
        query: 'content cannibalization',
        ownedBy: '/learn/fix-content-cannibalization-win-ai-traffic',
      },
    ],
    status: 'live',
    summary:
      'One page per search intent, titles and headings that match it, and internal links that connect the cluster.',
    metaTitle: 'On-Page SEO Services: Intent, Content & Links',
    description:
      'On-page SEO built on a topic map: one page per search intent, titles and headings that match what searchers want, and internal links that tie it together.',
    h1: 'On-Page SEO Services',
    secondaryQueries: [
      'on-page seo agency',
      'internal linking services',
      'seo content optimization',
    ],
    growthSystems: ['brand-awareness', 'lead-generation'],
    stack: ['google-search-console', 'ahrefs', 'semrush', 'ubersuggest'],
    learnSlugs: [
      'seo-101-a-beginners-working-guide',
      'fix-content-cannibalization-win-ai-traffic',
      'content-strategy-101-a-foundational-guide-for-success',
    ],
    reviewed: '2026-09-29',
  },
  {
    service: 'seo',
    slug: 'off-page-seo',
    name: 'Off-Page SEO',
    targetQuery: 'off-page seo services',
    notFor: [
      { query: 'local citations', ownedBy: '/services/seo/local-seo' },
      { query: 'google business profile', ownedBy: '/services/seo/local-seo' },
    ],
    status: 'live',
    summary:
      'A list of credible authorities, and content and tools worth citing — earning mentions and links without buying them.',
    metaTitle: 'Off-Page SEO & Link Building Services',
    description:
      'Off-page SEO without bought links: a list of credible authorities in your industry, and content and tools worth citing, placed where they earn mentions.',
    h1: 'Off-Page SEO Services',
    secondaryQueries: ['link building services', 'digital pr for seo', 'brand mentions'],
    growthSystems: ['brand-awareness'],
    stack: ['ahrefs', 'semrush', 'google-search-console'],
    learnSlugs: [
      'seo-101-a-beginners-working-guide',
      'content-strategy-101-a-foundational-guide-for-success',
    ],
    reviewed: '2026-09-29',
  },
  {
    service: 'seo',
    slug: 'technical-seo',
    name: 'Technical SEO',
    targetQuery: 'technical seo services',
    notFor: [
      { query: 'technical seo audit', ownedBy: '/tools/technical-seo-audit' },
      { query: 'technical seo checklist', ownedBy: '/learn (planned article)' },
    ],
    status: 'live',
    summary: 'Crawlability, indexation, Core Web Vitals, structured data and AI-crawler access.',
    metaTitle: 'Technical SEO Services',
    description:
      'Technical SEO that finds what stops search engines and AI crawlers reading your site, fixes it in order of impact, and keeps it fixed.',
    h1: 'Technical SEO Services',
    secondaryQueries: ['technical seo agency', 'technical seo consultant'],
    growthSystems: ['brand-awareness', 'lead-generation'],
    stack: ['google-search-console', 'pagespeed-insights', 'ahrefs', 'semrush', 'ubersuggest'],
    learnSlugs: [
      'mobile-first-indexing-what-you-need-to-know',
      'fix-content-cannibalization-win-ai-traffic',
      'seo-101-a-beginners-working-guide',
    ],
    reviewed: '2026-09-28',
  },
  {
    service: 'seo',
    slug: 'local-seo',
    name: 'Local SEO',
    targetQuery: 'local seo services',
    notFor: [
      { query: 'marketing agency <city>', ownedBy: 'nobody — location pages are policy-excluded' },
      { query: 'automotive marketing', ownedBy: '/automotive-marketing' },
      { query: 'brand awareness services', ownedBy: 'the Brand Awareness growth system' },
    ],
    status: 'live',
    summary: 'Google Business Profile, NAP consistency, local citations and on-site local depth.',
    metaTitle: 'Local SEO Services & Google Business Profile',
    description:
      'Local SEO that wins the map pack: an optimized Google Business Profile, consistent NAP across every listing, credible citations and local site depth.',
    h1: 'Local SEO Services',
    secondaryQueries: ['google business profile management', 'nap consistency', 'local citations'],
    growthSystems: ['brand-awareness'],
    stack: ['google-business-profile', 'google-search-console', 'ahrefs', 'semrush', 'ubersuggest'],
    learnSlugs: ['seo-101-a-beginners-working-guide'],
    reviewed: '2026-09-28',
  },
  {
    service: 'seo',
    slug: 'ai-search-optimization',
    name: 'AI Search Optimization',
    targetQuery: 'ai search optimization services',
    notFor: [
      {
        query: 'content cannibalization',
        ownedBy: '/learn/fix-content-cannibalization-win-ai-traffic',
      },
    ],
    status: 'planned',
    note: 'Proof is the audit tool’s AI-crawler checks; ship after the core four.',
  },
  // --- Paid Media ---
  {
    service: 'paid-media',
    slug: 'google-ads',
    name: 'Google Ads',
    targetQuery: 'google ads management',
    notFor: [
      { query: 'paid media agency', ownedBy: '/services/paid-media' },
      { query: 'performance max for local', ownedBy: '/learn (planned article)' },
    ],
    status: 'live',
    summary:
      'Search and Performance Max built on local assets, competitive research and tested cycles.',
    metaTitle: 'Google Ads Management & PPC Services',
    description:
      'Google Ads management for Search and Performance Max: local and industry asset groups, competitor research, budgets scaled in tested cycles.',
    h1: 'Google Ads Management',
    secondaryQueries: ['ppc management', 'performance max agency', 'google ads agency'],
    growthSystems: ['lead-generation', 'brand-awareness'],
    stack: ['google-ads', 'google-analytics-4', 'google-tag-manager'],
    learnSlugs: [
      'paid-media-101-a-starters-guide-to-advertising-success',
      'diversify-or-die-paid-media-strategy',
      'understanding-marketing-campaign-objectives-a-comprehensive-guide',
    ],
    reviewed: '2026-09-28',
  },
  {
    service: 'paid-media',
    slug: 'meta-ads',
    name: 'Meta Ads',
    targetQuery: 'meta ads management',
    notFor: [
      { query: 'abo vs cbo', ownedBy: '/learn (planned article)' },
      { query: 'organic social', ownedBy: '/services/content-strategy/content-distribution' },
    ],
    status: 'live',
    summary: 'Facebook and Instagram ads run as a test-and-scale pipeline from ABO to CBO.',
    metaTitle: 'Meta Ads Management: Facebook & Instagram',
    description:
      'Meta ads management run as a pipeline: creative tested in ABO against one to three output metrics, winners scaled in CBO, refined by season.',
    h1: 'Meta Ads Management',
    secondaryQueries: ['facebook ads management', 'instagram ads agency', 'meta ads agency'],
    growthSystems: ['lead-generation', 'brand-awareness'],
    stack: ['meta', 'google-analytics-4', 'google-tag-manager', 'canva'],
    learnSlugs: [
      'paid-media-101-a-starters-guide-to-advertising-success',
      'target-audience-101-a-short-guide-to-audience-targeting',
      'diversify-or-die-paid-media-strategy',
    ],
    reviewed: '2026-09-28',
  },
  {
    service: 'paid-media',
    slug: 'tiktok-ads',
    name: 'TikTok Ads',
    targetQuery: 'tiktok ads agency',
    notFor: [
      { query: 'organic tiktok', ownedBy: '/services/content-strategy/content-distribution' },
    ],
    status: 'live',
    summary:
      'Interest targeting for reach, then segmented retargeting of warm audiences and customer lists, measured server-side.',
    metaTitle: 'TikTok Ads Agency: Paid TikTok Campaigns',
    description:
      'TikTok ads run as a funnel: interest targeting for reach, then segmented retargeting of warm audiences and customer lists, measured with the Events API.',
    h1: 'TikTok Ads Agency',
    secondaryQueries: [
      'tiktok advertising agency',
      'tiktok ads management',
      'tiktok ads for ecommerce',
    ],
    growthSystems: ['lead-generation', 'brand-awareness'],
    stack: ['tiktok', 'google-analytics-4', 'google-tag-manager'],
    learnSlugs: [
      'paid-media-101-a-starters-guide-to-advertising-success',
      'target-audience-101-a-short-guide-to-audience-targeting',
      'diversify-or-die-paid-media-strategy',
    ],
    reviewed: '2026-09-29',
  },
  {
    service: 'paid-media',
    slug: 'snapchat-ads',
    name: 'Snapchat Ads',
    targetQuery: 'snapchat ads agency',
    notFor: [{ query: 'tiktok ads agency', ownedBy: '/services/paid-media/tiktok-ads' }],
    status: 'live',
    summary:
      'Snap Ads and creator content for reach, then segmented retargeting and customer lists, measured with the Conversions API.',
    metaTitle: 'Snapchat Ads Agency: Snap Ads Management',
    description:
      'Snapchat ads run as a funnel: Snap Ads and creator content for reach, then segmented retargeting and customer lists, measured with the Conversions API.',
    h1: 'Snapchat Ads Agency',
    secondaryQueries: [
      'snapchat advertising agency',
      'snap ads management',
      'snapchat marketing agency',
    ],
    growthSystems: ['brand-awareness', 'lead-generation'],
    stack: ['snapchat', 'google-analytics-4', 'google-tag-manager'],
    learnSlugs: [
      'paid-media-101-a-starters-guide-to-advertising-success',
      'target-audience-101-a-short-guide-to-audience-targeting',
      'diversify-or-die-paid-media-strategy',
    ],
    reviewed: '2026-09-29',
  },
  {
    service: 'paid-media',
    slug: 'creative-testing',
    name: 'Creative Testing',
    targetQuery: 'ad creative testing',
    notFor: [
      { query: 'meta ads management', ownedBy: '/services/paid-media/meta-ads' },
      { query: 'creative testing framework', ownedBy: '/learn (planned article)' },
    ],
    status: 'live',
    summary:
      'Angles, hooks and formats tested one variable at a time, with organic signal first and winners graduated to scale.',
    metaTitle: 'Ad Creative Testing: Angles, Hooks & Formats',
    description:
      'Ad creative testing that changes one variable at a time — angle, hook, then format — with organic signal first and a winner rule agreed before launch.',
    h1: 'Ad Creative Testing',
    secondaryQueries: [
      'creative testing agency',
      'ad creative testing services',
      'creative testing for paid social',
    ],
    growthSystems: ['lead-generation'],
    stack: ['meta', 'tiktok', 'canva', 'google-analytics-4'],
    learnSlugs: [
      'paid-media-101-a-starters-guide-to-advertising-success',
      'target-audience-101-a-short-guide-to-audience-targeting',
      'diversify-or-die-paid-media-strategy',
    ],
    reviewed: '2026-10-01',
  },
  {
    service: 'paid-media',
    slug: 'google-ad-grants',
    name: 'Google Ad Grants',
    targetQuery: 'google ad grants management',
    notFor: [{ query: 'google ads management', ownedBy: '/services/paid-media/google-ads' }],
    status: 'planned',
    note: 'Proof: LIE Foundation and The Bolden Experience case studies.',
  },
  // --- Content Strategy ---
  {
    service: 'content-strategy',
    slug: 'content-audit',
    name: 'Content Audit',
    targetQuery: 'content audit services',
    notFor: [
      { query: 'technical seo audit', ownedBy: '/tools/technical-seo-audit' },
      {
        query: 'what is a content audit',
        ownedBy: '/learn/content-strategy-101-a-foundational-guide-for-success',
      },
      { query: 'how to run a content audit', ownedBy: '/learn (planned article)' },
    ],
    status: 'live',
    summary:
      'An asset library of everything you have published, benchmarked against search, social and share-of-voice data.',
    metaTitle: 'Content Audit Services: Benchmark What Works',
    description:
      'Content audit services that build an asset library, then benchmark it with Search Console, native social analytics, Semrush share of voice and citations.',
    h1: 'Content Audit Services',
    secondaryQueries: ['content audit agency', 'seo content audit', 'social media content audit'],
    growthSystems: ['brand-awareness'],
    stack: ['google-search-console', 'semrush', 'google-analytics-4', 'meta'],
    learnSlugs: [
      'content-strategy-101-a-foundational-guide-for-success',
      'fix-content-cannibalization-win-ai-traffic',
      'how-to-measure-content-success',
    ],
    reviewed: '2026-10-01',
  },
  {
    service: 'content-strategy',
    slug: 'audience-research',
    name: 'Audience & Topic Research',
    targetQuery: 'audience research services',
    notFor: [
      {
        query: 'target audience',
        ownedBy: '/learn/target-audience-101-a-short-guide-to-audience-targeting',
      },
      { query: 'topic cluster tool', ownedBy: '/tools/topic-cluster-builder' },
    ],
    status: 'live',
    summary:
      'Who your buyers are and what they search for, built from search data, first-party data and platform audiences.',
    metaTitle: 'Audience & Topic Research Services',
    description:
      'Audience and topic research that maps what your buyers search for and who they are, from search demand, first-party customer data and platform audiences.',
    h1: 'Audience & Topic Research Services',
    secondaryQueries: [
      'audience research agency',
      'topic research services',
      'keyword and audience research',
    ],
    growthSystems: ['brand-awareness', 'lead-generation'],
    stack: ['semrush', 'google-search-console', 'google-analytics-4', 'meta'],
    learnSlugs: [
      'target-audience-101-a-short-guide-to-audience-targeting',
      'content-strategy-101-a-foundational-guide-for-success',
      'understanding-marketing-campaign-objectives-a-comprehensive-guide',
    ],
    reviewed: '2026-10-11',
  },
  {
    service: 'content-strategy',
    slug: 'content-production',
    name: 'Content Production',
    targetQuery: 'content production services',
    notFor: [
      {
        query: 'content calendar',
        ownedBy: '/learn/creating-a-content-calendar-how-to-guides-templates',
      },
    ],
    status: 'live',
    summary:
      'Pillar content on one home platform, produced so its performance decides what gets amplified or deepened.',
    metaTitle: 'Content Production Services: Pillar Content',
    description:
      'Content production built around pillar content on one home platform, with sources, visuals and tracking built in so performance decides what comes next.',
    h1: 'Content Production Services',
    secondaryQueries: [
      'content production agency',
      'pillar content creation',
      'long-form content production',
    ],
    growthSystems: ['brand-awareness'],
    stack: ['sanity', 'canva', 'google-search-console', 'google-analytics-4'],
    learnSlugs: [
      'creating-a-content-calendar-how-to-guides-templates',
      'content-strategy-101-a-foundational-guide-for-success',
      'how-to-measure-content-success',
    ],
    reviewed: '2026-10-01',
  },
  {
    service: 'content-strategy',
    slug: 'content-distribution',
    name: 'Content Distribution',
    targetQuery: 'content distribution services',
    notFor: [
      {
        query: 'how to repurpose content',
        ownedBy:
          '/learn/content-repurposing-101-how-to-maximize-reach-engagement-and-roi-from-every-post',
      },
      { query: 'tiktok ads agency', ownedBy: '/services/paid-media/tiktok-ads' },
    ],
    status: 'live',
    summary:
      'Each pillar piece repurposed into native placements — pins, carousels, short video — scheduled on each platform.',
    metaTitle: 'Content Distribution & Repurposing Services',
    description:
      'Content distribution that repurposes each pillar piece into pins, carousels and short video, published with native schedulers and measured per placement.',
    h1: 'Content Distribution Services',
    secondaryQueries: [
      'content repurposing services',
      'content distribution agency',
      'content repurposing agency',
    ],
    growthSystems: ['brand-awareness'],
    stack: ['canva', 'pinterest', 'meta', 'tiktok', 'snapchat'],
    learnSlugs: [
      'content-strategy-101-a-foundational-guide-for-success',
      'creating-a-content-calendar-how-to-guides-templates',
      'how-to-measure-content-success',
    ],
    reviewed: '2026-10-01',
  },
  {
    service: 'content-strategy',
    slug: 'content-goals',
    name: 'Content Goals & KPIs',
    targetQuery: 'content strategy goals',
    notFor: [
      { query: 'content kpis', ownedBy: '/learn/how-to-measure-content-success' },
      { query: 'marketing analytics services', ownedBy: '/services/measurement' },
    ],
    status: 'live',
    summary:
      'Goals tied to business outcomes, each with the leading indicators and revenue measures that prove progress.',
    metaTitle: 'Content Strategy Goals & KPI Framework',
    description:
      'Content goals set against business outcomes, with a KPI ladder from leading indicators to revenue so every piece is judged on the job it was made to do.',
    h1: 'Content Strategy Goals & KPIs',
    secondaryQueries: ['content marketing goals', 'content kpi framework', 'content goal setting'],
    growthSystems: ['brand-awareness'],
    stack: ['google-analytics-4', 'google-search-console'],
    learnSlugs: [
      'how-to-measure-content-success',
      'understanding-marketing-campaign-objectives-a-comprehensive-guide',
      'marketing-analytics-101-how-to-measure-analyze-and-optimize-campaign-performance',
    ],
    reviewed: '2026-10-11',
  },

  // --- Measurement ---
  {
    service: 'measurement',
    slug: 'conversion-tracking',
    name: 'Conversion Tracking',
    targetQuery: 'conversion tracking services',
    notFor: [
      { query: 'utm builder', ownedBy: '/tools/utm-builder' },
      { query: 'meta ads management', ownedBy: '/services/paid-media/meta-ads' },
      { query: 'what is conversions api', ownedBy: '/learn/pixel-vs-conversion-api-tracking' },
    ],
    status: 'live',
    summary:
      'Pixels, Tag Manager and server-side Conversions APIs set up so every platform sees the conversions it drives.',
    metaTitle: 'Conversion Tracking Services: CAPI & GA4',
    description:
      'Conversion tracking that survives browser signal loss: GA4 and Tag Manager, pixels paired with server-side Conversions APIs, and SKAdNetwork for iOS apps.',
    h1: 'Conversion Tracking Services',
    secondaryQueries: [
      'conversions api setup',
      'server-side tracking services',
      'meta capi implementation',
    ],
    growthSystems: ['lead-generation', 'marketing-automation'],
    stack: ['google-analytics-4', 'google-tag-manager', 'meta', 'tiktok', 'snapchat'],
    learnSlugs: [
      'pixel-vs-conversion-api-tracking',
      'marketing-analytics-101-how-to-measure-analyze-and-optimize-campaign-performance',
      'marketing-attribution-101',
    ],
    reviewed: '2026-10-01',
  },
  {
    service: 'measurement',
    slug: 'attribution-modeling',
    name: 'Attribution Modeling',
    targetQuery: 'marketing attribution services',
    notFor: [
      { query: 'marketing attribution models', ownedBy: '/learn/marketing-attribution-101' },
      { query: 'roas calculator', ownedBy: '/tools/roas-calculator' },
    ],
    status: 'live',
    summary:
      'Multi-touch attribution that gives each channel credited weight across the whole journey, not just the last click.',
    metaTitle: 'Marketing Attribution Services: Multi-Touch',
    description:
      'Marketing attribution services that map multi-touch journeys across paid, organic and product channels and give each one credited weight in the model.',
    h1: 'Marketing Attribution Services',
    secondaryQueries: [
      'attribution modeling services',
      'multi-touch attribution services',
      'marketing attribution consultant',
    ],
    growthSystems: ['lead-generation'],
    stack: ['google-analytics-4', 'google-tag-manager'],
    learnSlugs: [
      'marketing-attribution-101',
      'marketing-analytics-101-how-to-measure-analyze-and-optimize-campaign-performance',
      'how-to-measure-content-success',
    ],
    reviewed: '2026-10-01',
  },
]

export const liveTopics: LiveTopic[] = SERVICE_TOPICS.filter(
  (t): t is LiveTopic => t.status === 'live'
)

export function topicPath(t: { service: ServiceSlug; slug: string }): string {
  return `${servicePath(t.service)}/${t.slug}`
}

export function liveTopicsFor(service: ServiceSlug): LiveTopic[] {
  return liveTopics.filter((t) => t.service === service)
}

/** A live topic by service + slug, or undefined when missing or still planned. */
export function findLiveTopic(service: ServiceSlug, slug: string): LiveTopic | undefined {
  return liveTopics.find((t) => t.service === service && t.slug === slug)
}

/**
 * A live topic, or a thrown error — for the topic's own page, so a page whose
 * registry entry is missing or still `planned` fails the build instead of
 * rendering with undefined metadata.
 */
export function requireLiveTopic(service: ServiceSlug, slug: string): LiveTopic {
  const topic = findLiveTopic(service, slug)
  if (!topic) throw new Error(`No live service topic ${service}/${slug} in lib/service-topics.ts`)
  return topic
}

/**
 * Where to link for a topic: its own page once live, its parent service until
 * then. Lets a growth system name `local-seo` today and have the link upgrade
 * itself the day that page ships, with no second edit.
 */
export function topicOrServiceLink(
  service: ServiceSlug,
  slug: string
): { href: string; label: string } {
  const topic = SERVICE_TOPICS.find((t) => t.service === service && t.slug === slug)
  if (!topic) throw new Error(`No service topic ${service}/${slug} in lib/service-topics.ts`)
  return topic.status === 'live'
    ? { href: topicPath(topic), label: topic.name }
    : { href: servicePath(service), label: getService(service).name }
}

/** Page metadata for a live topic, through the site's one metadata helper. */
export function topicMetadata(t: LiveTopic): Metadata {
  return pageMetadata({ path: topicPath(t), title: t.metaTitle, description: t.description })
}

/** Live topics that feed a growth system, for its "how it's delivered" links. */
export function topicsForGrowthSystem(id: GrowthSystemId): LiveTopic[] {
  return liveTopics.filter((t) => t.growthSystems.includes(id))
}
